import React, { createContext, useContext } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children, language: languageProp = 'ar' }) => {
  const translations = {
    en: {
      brand: {
        name: 'Masar Tech',
        logoAlt: 'Masar Tech logo',
      },
      nav: {
        home: 'Home',
        portfolio: 'Portfolio',
        about: 'About',
        contact: 'Contact',
        demo: 'Academy Demo'
      },
      hero: {
        title: 'We Build',
        titleHighlight1: 'Websites',
        titleHighlight2: 'Bots',
        titleHighlight3: 'Automations',
        tagline: 'Transforming your digital presence with cutting-edge web solutions, intelligent AI bots, and seamless workflow automations that drive growth and efficiency.',
        cta: 'Get a Quote'
      },
      featuredProject: {
        badge: 'Featured Project',
        title: 'Sports Academy Management System',
        description: 'A comprehensive management platform for sports academies with multi-branch support, online payments (Mada, STC Pay, Apple Pay), SMS notifications, attendance tracking, and full Arabic/RTL support for the Saudi market.',
        viewDemo: 'View Live Demo',
        learnMore: 'Learn More',
      },
      forAcademies: {
        badge: 'Our Featured Project',
        heroTitle1: 'Manage Your',
        heroTitle2: 'Sports Academy',
        heroTitle3: 'From One Place',
        heroDesc: 'A complete platform for multi-branch sports academies in Saudi Arabia. Registration, payments, attendance, evaluations, and parent communication—all in one system.',
        watchDemo: 'Watch Demo',
        requestDemo: 'Request Demo',
        videoBadge: '1-Minute Demo',
        videoTitle: 'See the platform in action',
        featuresBadge: 'Features',
        featuresTitle: 'Everything You Need',
        contactBadge: 'Get Started',
        contactTitle: 'Request a Demo',
        contactDesc: "Fill the form and we'll contact you within 24 hours.",
        form: {
          academyName: 'Academy Name',
          contactName: 'Contact Name',
          phone: 'Phone Number',
          email: 'Email',
          message: 'Message (optional)',
          submit: 'Send Request',
          whatsapp: 'WhatsApp',
        },
      },
      projects: {
        title: 'Our Work / Portfolio',
        subtitle: 'Explore our latest projects and solutions',
        loading: 'Loading projects...',
        items: [
          {
            title: 'Creating websites',
            description: 'Professional website development with modern design and responsive layouts.'
          },
          {
            title: 'Customer Support Bot',
            description: 'Intelligent chatbot handling 24/7 customer inquiries with natural language processing.'
          },
          {
            title: 'Workflow Automation System',
            description: 'Streamlined business processes with automated task management and reporting.'
          },
          {
            title: 'SaaS Dashboard',
            description: 'Beautiful analytics dashboard with real-time data visualization and insights.'
          },
          {
            title: 'E-Commerce Platform',
            description: 'Modern e-commerce solution with AI-powered recommendations and automated inventory management.'
          },
          {
            title: 'Mobile Applications',
            // description: 'We build simple and practical mobile applications that work on iOS and Android devices, specifically designed to meet your business needs, with a smooth user experience and robust core functionality.'
            description: "We build simple, functional mobile apps for Android and iOS quickly and efficiently to meet your business needs. "
          }
        ]
      },
      about: {
        title: 'About Us',
        description1: 'At Masar Tech, we are passionate about innovating advanced technical solutions that help businesses thrive in the digital age.',
        description2: 'With experience in web development, artificial intelligence, and smart automation, we deliver solutions specifically designed to meet your business needs and achieve your commercial goals.',
        description3: 'Our team combines creative design and technical expertise to build:',
        bullet1: 'Professional websites',
        bullet2: 'Intelligent AI-powered bots',
        bullet3: 'Workflow automation to accelerate processes and reduce manual effort',
        description4: "We don't just design solutions that look great, but achieve real business results and make it easier for your organization to grow and expand.",
        strengths: {
          design: {
            title: 'Creative Design',
            desc: 'Beautiful, modern designs that captivate and convert'
          },
          delivery: {
            title: 'Fast Delivery',
            desc: 'Quick turnaround times without compromising quality'
          },
          automation: {
            title: 'Automation Expertise',
            desc: 'Streamline workflows and boost productivity'
          },
          bots: {
            title: 'AI Bots',
            desc: 'Intelligent chatbots that enhance customer experience'
          },
          webdev: {
            title: 'Web Development',
            desc: 'Scalable, responsive websites built with latest technologies'
          },
          growth: {
            title: 'Growth Focused',
            desc: 'Solutions designed to drive business growth'
          }
        }
      },
      contact: {
        title: 'Get In Touch',
        subtitle: "Let's discuss your next project",
        email: 'Email',
        phone: 'Phone',
        follow: 'Follow Us',
        form: {
          name: 'Name',
          namePlaceholder: 'Your name',
          email: 'Email',
          emailPlaceholder: 'your.email@example.com',
          message: 'Message',
          messagePlaceholder: 'Tell us about your project...',
          send: 'Send Message',
          sending: 'Sending...',
          success: 'Thank you for your message! We will get back to you soon.',
          error: 'Failed to send message. Please try again.'
        }
      },
      footer: {
        rights: 'All rights reserved.'
      }
    },
    ar: {
      brand: {
        name: 'مسار - تقنية',
        logoAlt: 'مسار - تقنية',
      },
      nav: {
        home: 'الرئيسية',
        portfolio: 'الأعمال',
        about: 'من نحن',
        contact: 'اتصل بنا',
        demo: 'عرض الأكاديمية'
      },
      hero: {
        title: 'نحن نبني',
        titleHighlight1: 'مواقع الويب',
        titleHighlight2: 'بوتات',
        titleHighlight3: 'الأتمتة',
        tagline: 'نحول وجودك الرقمي من خلال حلول ويب متطورة، روبوتات ذكية، وأتمتة سلسة للعمليات التي تدفع النمو والكفاءة.',
        cta: 'احصل على عرض سعر'
      },
      featuredProject: {
        badge: 'أبرز المشاريع',
        title: 'نظام إدارة الأكاديميات الرياضية',
        description: 'منصة شاملة لإدارة الأكاديميات الرياضية مع دعم الفروع المتعددة، المدفوعات الإلكترونية (مدى، STC Pay، Apple Pay)، إشعارات SMS، تتبع الحضور، ودعم كامل للعربية واتجاه RTL للسوق السعودي.',
        viewDemo: 'عرض التجربة الحية',
        learnMore: 'اعرف المزيد',
      },
      forAcademies: {
        badge: 'أبرز المشاريع',
        heroTitle1: 'أدر',
        heroTitle2: 'أكاديميتك الرياضية',
        heroTitle3: 'من مكان واحد',
        heroDesc: 'منصة متكاملة لأكاديميات رياضية متعددة الفروع في المملكة. التسجيل، المدفوعات، الحضور، التقييمات، والتواصل مع أولياء الأمور—كل ذلك في نظام واحد.',
        watchDemo: 'شاهد العرض',
        requestDemo: 'اطلب عرضاً',
        videoBadge: 'عرض دقيقة واحدة',
        videoTitle: 'شاهد المنصة في العمل',
        featuresBadge: 'المميزات',
        featuresTitle: 'كل ما تحتاجه',
        contactBadge: 'ابدأ الآن',
        contactTitle: 'اطلب عرضاً توضيحياً',
        contactDesc: 'املأ النموذج وسنتواصل معك خلال 24 ساعة.',
        form: {
          academyName: 'اسم الأكاديمية',
          contactName: 'اسم المسؤول',
          phone: 'رقم الجوال',
          email: 'البريد الإلكتروني',
          message: 'رسالة (اختياري)',
          submit: 'إرسال الطلب',
          whatsapp: 'واتساب',
        },
      },
      projects: {
        title: 'أعمالنا  وخدماتنا',
        subtitle: 'استكشف أحدث مشاريعنا وحلولنا',
        loading: 'جاري تحميل المشاريع...',
        items: [
          {
            title: 'إنشاء المواقع',
            description: 'تطوير مواقع ويب احترافية بتصميم عصري وتخطيطات متجاوبة.'
          },
          {
            title: 'بوت دعم العملاء',
            description: 'بوت محادثة ذكي يتعامل مع استفسارات العملاء على مدار الساعة باستخدام معالجة اللغة الطبيعية.'
          },
          {
            title: 'نظام أتمتة سير العمل',
            description: 'عمليات أعمال مبسطة مع إدارة مهام آلية وإعداد تقارير.'
          },
          {
            title: 'لوحة تحكم SaaS',
            description: 'لوحة تحليلات جميلة مع تصور بيانات في الوقت الفعلي ورؤى.'
          },
          {
            title: 'منصة التجارة الإلكترونية',
            description: 'حل تجارة إلكترونية حديث مع توصيات مدعومة بالذكاء الاصطناعي وإدارة مخزون آلية.'
          },
          {
            title: ' تطبيقات الجوال',
            // description: 'نقوم ببناء تطبيقات جوال بسيطة وعملية تعمل على أجهزة iOS و Android، مصمّمة خصيصًا لتلبية احتياجات عملك، مع تجربة مستخدم سلسة ووظائف أساسية قوية.'
            description: "نبني تطبيقات جوال بسيطة وعملية تعمل على Android و iOS لتلبية احتياجات عملك بسرعة وكفاءة. ",
          }
        ]
      },
      about: {
        title: 'من نحن',
        description1: 'نحن «مسار - تقنية»، شغوفون بابتكار حلول تقنية متقدمة تساعد الشركات على الازدهار في العصر الرقمي.',
        description2: 'بخبرة في تطوير الويب، الذكاء الاصطناعي، والأتمتة الذكية، نقدم حلولًا مصمّمة خصيصًا لتلبية احتياجات عملك وتحقيق أهدافك التجارية.',
        description3: 'يجمع فريقنا بين التصميم الإبداعي والخبرة التقنية لبناء:',
        bullet1: 'مواقع إلكترونية احترافية',
        bullet2: 'بوتات ذكية تعتمد على الذكاء الاصطناعي',
        bullet3: 'أتمتة سير العمل لتسريع العمليات وتقليل الجهد اليدوي',
        description4: 'نحن لا نصمم حلولًا تبدو رائعة فقط، بل تُحقّق نتائج أعمال حقيقية وتُسهّل على مؤسستك النمو والتوسع.',
        strengths: {
          design: {
            title: 'تصميم إبداعي',
            desc: 'تصاميم جميلة وعصرية تجذب وتحول'
          },
          delivery: {
            title: 'تسليم سريع',
            desc: 'أوقات تسليم سريعة دون المساس بالجودة'
          },
          automation: {
            title: 'خبرة في الأتمتة',
            desc: 'تبسيط سير العمل وزيادة الإنتاجية'
          },
          bots: {
            title: 'بوتات ذكية',
            desc: 'بوتات محادثة ذكية تعزز تجربة العملاء'
          },
          webdev: {
            title: 'تطوير الويب',
            desc: 'مواقع ويب قابلة للتوسع ومتجاوبة مبنية بأحدث التقنيات'
          },
          growth: {
            title: 'مركز على النمو',
            desc: 'حلول مصممة لدفع نمو الأعمال'
          }
        }
      },
      contact: {
        title: 'تواصل معنا',
        subtitle: 'دعنا نناقش مشروعك القادم',
        email: 'البريد الإلكتروني',
        phone: 'الهاتف',
        follow: 'تابعنا',
        form: {
          name: 'الاسم',
          namePlaceholder: 'اسمك',
          email: 'البريد الإلكتروني',
          emailPlaceholder: 'بريدك@example.com',
          message: 'الرسالة',
          messagePlaceholder: 'أخبرنا عن مشروعك...',
          send: 'إرسال الرسالة',
          sending: 'جاري الإرسال...',
          success: 'شكراً لرسالتك! سنعود إليك قريباً.',
          error: 'فشل إرسال الرسالة. يرجى المحاولة مرة أخرى.'
        }
      },
      footer: {
        rights: 'جميع الحقوق محفوظة.'
      }
    },
    tr: {
      brand: {
        name: 'Masar Tech',
        logoAlt: 'Masar Tech logosu',
      },
      nav: {
        home: 'Ana Sayfa',
        portfolio: 'Portföy',
        about: 'Hakkımızda',
        contact: 'İletişim',
        demo: 'Akademi Demosu'
      },
      hero: {
        title: 'Biz İnşa Ediyoruz',
        titleHighlight1: 'Web Siteleri',
        titleHighlight2: 'Botlar',
        titleHighlight3: 'Otomasyonlar',
        tagline: 'Dijital varlığınızı, büyümeyi ve verimliliği artıran son teknoloji web çözümleri, akıllı AI botları ve sorunsuz iş akışı otomasyonlarıyla dönüştürüyoruz.',
        cta: 'Teklif Al'
      },
      featuredProject: {
        badge: 'Öne Çıkan Proje',
        title: 'Spor Akademisi Yönetim Sistemi',
        description: 'Çok şubeli destek, online ödemeler (Mada, STC Pay, Apple Pay), SMS bildirimleri, devam takibi ve Suudi pazarı için tam Arapça/RTL desteği ile spor akademileri için kapsamlı bir yönetim platformu.',
        viewDemo: 'Canlı Demoyu Görüntüle',
        learnMore: 'Daha Fazla Bilgi',
      },
      forAcademies: {
        badge: 'Öne Çıkan Proje',
        heroTitle1: 'Yönetin',
        heroTitle2: 'Spor Akademinizi',
        heroTitle3: 'Tek Yerden',
        heroDesc: 'Suudi Arabistan\'da çok şubeli spor akademileri için eksiksiz bir platform. Kayıt, ödemeler, devam, değerlendirmeler ve veli iletişimi—hepsi tek bir sistemde.',
        watchDemo: 'Demoyu İzle',
        requestDemo: 'Demo Talep Et',
        videoBadge: '1 Dakikalık Demo',
        videoTitle: 'Platformu çalışırken görün',
        featuresBadge: 'Özellikler',
        featuresTitle: 'İhtiyacınız Olan Her Şey',
        contactBadge: 'Başlayın',
        contactTitle: 'Demo Talep Edin',
        contactDesc: 'Formu doldurun, 24 saat içinde sizinle iletişime geçeceğiz.',
        form: {
          academyName: 'Akademi Adı',
          contactName: 'İletişim Adı',
          phone: 'Telefon Numarası',
          email: 'E-posta',
          message: 'Mesaj (isteğe bağlı)',
          submit: 'Talep Gönder',
          whatsapp: 'WhatsApp',
        },
      },
      projects: {
        title: 'Çalışmalarımız / Portföy',
        subtitle: 'En son projelerimizi ve çözümlerimizi keşfedin',
        loading: 'Projeler yükleniyor...',
        items: [
          {
            title: 'Web sitesi oluşturma',
            description: 'Modern tasarım ve duyarlı düzenlerle profesyonel web sitesi geliştirme.'
          },
          {
            title: 'Müşteri Destek Botu',
            description: 'Doğal dil işleme ile 7/24 müşteri sorgularını yöneten akıllı sohbet botu.'
          },
          {
            title: 'İş Akışı Otomasyon Sistemi',
            description: 'Otomatik görev yönetimi ve raporlama ile sadeleştirilmiş iş süreçleri.'
          },
          {
            title: 'SaaS Kontrol Paneli',
            description: 'Gerçek zamanlı veri görselleştirme ve içgörülerle güzel analitik kontrol paneli.'
          },
          {
            title: 'E-Ticaret Platformu',
            description: 'AI destekli öneriler ve otomatik envanter yönetimi ile modern e-ticaret çözümü.'
          },
          {
            title: 'Mobil Uygulamalar',
            // description: 'iOS ve Android cihazlarda çalışan, iş ihtiyaçlarınızı karşılamak için özel olarak tasarlanmış, sorunsuz bir kullanıcı deneyimi ve güçlü temel işlevlerle basit ve pratik mobil uygulamalar geliştiriyoruz.'
            description: "Android ve iOS için hızlı ve pratik mobil uygulamalar geliştiriyoruz."
          }
        ]
      },
      about: {
        title: 'Hakkımızda',
        description1: 'Masar Tech olarak, dijital çağda işletmelerin gelişmesine yardımcı olan gelişmiş teknik çözümler üretmeye tutkuluyuz.',
        description2: 'Web geliştirme, yapay zeka ve akıllı otomasyon konularındaki deneyimimizle, iş ihtiyaçlarınızı karşılamak ve ticari hedeflerinize ulaşmak için özel olarak tasarlanmış çözümler sunuyoruz.',
        description3: 'Ekibimiz şunları inşa etmek için yaratıcı tasarım ve teknik uzmanlığı birleştiriyor:',
        bullet1: 'Profesyonel web siteleri',
        bullet2: 'Akıllı AI destekli botlar',
        bullet3: 'Süreçleri hızlandırmak ve manuel çabayı azaltmak için iş akışı otomasyonu',
        description4: 'Sadece harika görünen çözümler tasarlamıyoruz, aynı zamanda gerçek iş sonuçları elde ediyor ve organizasyonunuzun büyümesini ve genişlemesini kolaylaştırıyoruz.',
        strengths: {
          design: {
            title: 'Yaratıcı Tasarım',
            desc: 'Büyüleyen ve dönüştüren güzel, modern tasarımlar'
          },
          delivery: {
            title: 'Hızlı Teslimat',
            desc: 'Kaliteden ödün vermeden hızlı teslimat süreleri'
          },
          automation: {
            title: 'Otomasyon Uzmanlığı',
            desc: 'İş akışlarını sadeleştirin ve verimliliği artırın'
          },
          bots: {
            title: 'AI Botları',
            desc: 'Müşteri deneyimini geliştiren akıllı sohbet botları'
          },
          webdev: {
            title: 'Web Geliştirme',
            desc: 'En son teknolojilerle inşa edilmiş ölçeklenebilir, duyarlı web siteleri'
          },
          growth: {
            title: 'Büyüme Odaklı',
            desc: 'İş büyümesini artırmak için tasarlanmış çözümler'
          }
        }
      },
      contact: {
        title: 'İletişime Geçin',
        subtitle: 'Bir sonraki projenizi tartışalım',
        email: 'E-posta',
        phone: 'Telefon',
        follow: 'Bizi Takip Edin',
        form: {
          name: 'İsim',
          namePlaceholder: 'Adınız',
          email: 'E-posta',
          emailPlaceholder: 'e-posta@example.com',
          message: 'Mesaj',
          messagePlaceholder: 'Projeniz hakkında bize bilgi verin...',
          send: 'Mesaj Gönder',
          sending: 'Gönderiliyor...',
          success: 'Mesajınız için teşekkürler! En kısa sürede size dönüş yapacağız.',
          error: 'Mesaj gönderilemedi. Lütfen tekrar deneyin.'
        }
      },
      footer: {
        rights: 'Tüm hakları saklıdır.'
      }
    }
  };

  const language = languageProp in translations ? languageProp : 'ar';
  const t = translations[language];

  return (
    <LanguageContext.Provider
      value={{
        language,
        toggleLanguage: () => {},
        setLanguage: () => {},
        t,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

